/**
 * HUD, menus and overlays. Plain DOM layered over the canvas.
 *
 * The UI is reactive: it subscribes to the event bus and mutates cached nodes
 * when something happens. `update` only handles what genuinely has to be
 * sampled every frame — the vitals readout and the fade timers — and it never
 * builds DOM unless a value actually changed.
 *
 * Two rules keep it out of the game's way:
 *  - `#ui-root` is `pointer-events: none`; only a visible menu turns them back
 *    on, so the crosshair layer can never eat a click during play.
 *  - the UI never touches pointer lock. Screens that can open mid-run (the
 *    inventory, the journal) are therefore fully keyboard-driven; releasing the
 *    lock here would make `core/input.ts` synthesise a pause.
 */

import type * as THREE from 'three';

import { TUNING } from '../core/config';
import type { JournalEntry, ResumeOffer, ScreenName, UIAction, UISystem } from '../core/systems';
import type { ComfortHost, EventBus, GameContext, GameSettings, InventorySlot, LevelSpec } from '../core/types';
import { el } from './dom';
import { Fade, type Veil } from './fade';
import { Hud } from './hud';
import { InventoryScreen } from './inventory';
import { ITEM_UI, type SlotView } from './items';
import { LevelCard } from './levelcard';
import { MapOverlay } from './map';
import { cloneSettings, DeadScreen, PausedScreen, SettingsScreen, TitleScreen } from './menus';
import { JournalScreen } from './notes';
import type { Screen } from './screen';
import { makeXRUi, type XRUi } from './xr';
import './styles.css';

/**
 * Screens that halt the simulation. The HUD and the level card do not.
 *
 * The map is not in here because it is not a `ScreenName` — `core/systems.ts`
 * owns that union and the UI does not. It is an overlay that reports itself
 * through `blocking` instead, which is the only thing `game.ts` actually reads.
 */
const BLOCKING: ReadonlySet<ScreenName> = new Set<ScreenName>([
  'title',
  'paused',
  'settings',
  'inventory',
  'dead',
  'journal',
]);

/** Overwritten by `setSettings` during `Game`'s constructor; never really used. */
function fallbackSettings(): GameSettings {
  return {
    masterVolume: TUNING.audio.masterVolume,
    muted: false,
    sensitivity: TUNING.player.sensitivity,
    invertY: false,
    subtitles: true,
    render: {
      quality: 'high',
      fov: TUNING.render.defaultFov,
      postFx: true,
      headBob: true,
      resolutionScale: 1,
    },
  };
}

class UI implements UISystem, Veil {
  /** Set properly by the constructor; the field only needs a legal value. */
  current: ScreenName = 'hud';

  private readonly el = el('div', 'ui');
  private readonly hud = new Hud();
  private readonly map = new MapOverlay();
  private readonly fade = new Fade();
  private readonly card = new LevelCard();
  private readonly screens: Partial<Record<ScreenName, Screen>> = {};
  private readonly screenList: Array<[ScreenName, Screen]> = [];

  private readonly title: TitleScreen;
  private readonly paused: PausedScreen;
  private readonly settingsScreen: SettingsScreen;
  private readonly inventory: InventoryScreen;
  private readonly journal: JournalScreen;
  private readonly dead: DeadScreen;

  private readonly handlers: Array<(a: UIAction) => void> = [];
  private readonly unsubscribe: Array<() => void> = [];

  /**
   * The same UI, painted onto quads in the room.
   *
   * DOM does not render inside an immersive WebXR session, so without this a
   * headset gets no prompt, no vitals, no subtitles, no map and no menus.
   * Constructing it costs a handful of event subscriptions; it builds nothing
   * and draws nothing until `renderer.xr.isPresenting` first goes true, and it
   * takes itself back out of the scene the moment it goes false. On a desktop
   * frame it is one boolean read. See `ui/xr/layer.ts`.
   */
  private readonly xr: XRUi;

  /** `MapOverlay` owns its canvas and does not publish it; found once, here. */
  private mapCanvasCache: HTMLCanvasElement | null = null;

  private settings: GameSettings = fallbackSettings();
  private settingsReturn: ScreenName = 'title';
  private journalReturn: ScreenName = 'hud';

  /** Reused snapshot of the inventory. Compared per frame, never reallocated. */
  private readonly slotViews: SlotView[] = Array.from({ length: TUNING.survival.inventorySlots }, () => ({
    kind: null,
    count: 0,
  }));

  private lastSpec: LevelSpec | null = null;
  private lastIndex = 0;
  private runActive = false;
  private cardArmed = false;

  constructor(root: HTMLElement, private readonly events: EventBus, comfort: ComfortHost) {
    const dispatch = (a: UIAction): void => this.dispatch(a);
    // The in-world layer reads live state through getters rather than a
    // snapshot; object-literal getters cannot be arrows, so they need this.
    const self = this;

    this.title = new TitleScreen(dispatch, () => this.openSettings());
    this.paused = new PausedScreen(dispatch, () => this.openSettings(), () => this.openJournal());
    this.settingsScreen = new SettingsScreen(this.settings, (s) => this.applySettings(s), () => this.show(this.settingsReturn));
    this.inventory = new InventoryScreen(dispatch);
    this.journal = new JournalScreen();
    this.dead = new DeadScreen(dispatch);

    // A dev-server hot reload builds a second Game without tearing this one
    // down, which would otherwise stack UI layers on top of each other.
    for (const stale of Array.from(root.querySelectorAll(':scope > .ui'))) stale.remove();

    this.el.appendChild(this.hud.el);
    // Over the HUD and under the veil: the map replaces the readout while it is
    // up, but a descent still owns the screen outright.
    this.el.appendChild(this.map.el);
    // Between the HUD and everything else: the descent takes the world and the
    // vitals away, but the level card has to sit *on* the black rather than
    // under it — the name of the floor resolving out of the dark is the whole
    // point of the pause. No blocking screen can be up mid-descent, so the
    // menus being painted over the veil never comes up.
    this.el.appendChild(this.fade.el);
    this.addScreen('title', this.title);
    this.addScreen('paused', this.paused);
    this.addScreen('settings', this.settingsScreen);
    this.addScreen('inventory', this.inventory);
    this.addScreen('journal', this.journal);
    this.addScreen('dead', this.dead);
    this.el.appendChild(this.card.el);
    root.appendChild(this.el);

    this.xr = makeXRUi({
      events,
      get current(): ScreenName {
        return self.current;
      },
      get settings(): GameSettings {
        return self.settings;
      },
      mapCanvas: () => this.mapCanvas(),
      get mapVisible(): boolean {
        return self.map.visible;
      },
      get mapFull(): boolean {
        return self.map.open;
      },
      dispatch,
      show: (screen) => this.show(screen),
      openSettings: () => this.openSettings(),
      closeSettings: () => this.show(this.settingsReturn),
      openJournal: () => this.openJournal(),
      closeJournal: () => this.show(this.journalReturn),
      applySettings: (s) => {
        this.applySettings(s);
        // The DOM settings screen keeps its own copy and only ever hears about
        // edits it made itself, so without this it would show stale values the
        // next time the headset came off. Deliberately not folded into
        // `applySettings`: that runs from the DOM controls' own `input`
        // handlers, and refreshing a range input mid-drag breaks the drag.
        this.settingsScreen.setSettings(s);
      },
      comfort,
    });

    this.wireEvents();
    window.addEventListener('keydown', this.onKeyDown);
    this.applyScreen('title', true);
  }

  /**
   * The map's canvas, for the in-world panel to hang as a texture.
   *
   * `ui/map.ts` already draws a perfectly good sheet of paper; reimplementing
   * it for the headset would be a second map to keep in step with the first.
   * Reached through the DOM rather than through a new accessor because the map
   * is not this batch's file to change, and it builds its canvas once in its
   * constructor and never replaces it — so one query is enough.
   */
  private mapCanvas(): HTMLCanvasElement | null {
    if (!this.mapCanvasCache) this.mapCanvasCache = this.map.el.querySelector('canvas');
    return this.mapCanvasCache;
  }

  private addScreen(name: ScreenName, screen: Screen): void {
    this.screens[name] = screen;
    this.screenList.push([name, screen]);
    screen.el.hidden = true;
    this.el.appendChild(screen.el);
  }

  /* ---------------------------------------------------------------- *
   * UISystem
   * ---------------------------------------------------------------- */

  get blocking(): boolean {
    return BLOCKING.has(this.current) || this.map.open;
  }

  show(screen: ScreenName): void {
    // The card is an overlay, not a panel: it plays over whatever is up and
    // leaves the screen underneath alone.
    if (screen === 'levelcard') {
      if (!this.lastSpec) return;
      this.card.play(this.lastSpec, () => {
        if (this.current === 'levelcard') this.applyScreen('hud');
      });
      if (this.current === 'hud' || this.current === 'title') this.applyScreen('levelcard');
      return;
    }
    this.applyScreen(screen);
  }

  toast(text: string, tone: 'info' | 'good' | 'bad' = 'info'): void {
    this.hud.toast(text, tone);
  }

  subtitle(text: string, ms?: number): void {
    this.hud.subtitle(text, ms);
  }

  veil(alpha: number, seconds: number): void {
    this.fade.veil(alpha, seconds);
  }

  onAction(fn: (action: UIAction) => void): void {
    this.handlers.push(fn);
  }

  setSettings(settings: GameSettings): void {
    this.settings = cloneSettings(settings);
    this.hud.setSubtitlesEnabled(this.settings.subtitles);
    this.settingsScreen.setSettings(this.settings);
    this.xr.settingsChanged();
  }

  setResume(offer: ResumeOffer | null): void {
    this.title.setResume(offer);
    this.xr.setResume(offer);
  }

  restoreJournal(notes: readonly JournalEntry[]): void {
    this.journal.restore(notes);
    this.xr.setJournal(notes);
  }

  exportFog(): Uint8Array | null {
    return this.map.exportFog();
  }

  importFog(fog: Uint8Array): void {
    this.map.importFog(fog);
  }

  /**
   * Straight through to the in-world layer, which is the only part of this
   * system that exists in three dimensions. False for the whole life of a
   * desktop, where there is no session and therefore no panel to point at.
   */
  claimsRay(ray: { origin: THREE.Vector3; direction: THREE.Vector3 } | null): boolean {
    return this.xr.claimsRay(ray);
  }

  update(ctx: GameContext): void {
    this.syncSlots(ctx.inventory);
    this.hud.update(ctx);
    this.updateMap(ctx);
    // Last, so the in-world map panel uploads a sheet that has already been
    // redrawn for this frame. Returns immediately when no session is running.
    this.xr.update(ctx);
  }

  /**
   * The map is toggled off the bound `'map'` action rather than off a raw
   * keydown, so `core/input.ts` stays the only place a key means anything. This
   * runs on every frame including the ones the map itself has halted, which is
   * what lets the same press close it again.
   */
  private updateMap(ctx: GameContext): void {
    if (ctx.input.pressed('map') && (this.map.visible || this.canOpenMap())) {
      this.map.cycle();
      this.syncBlocking();
    }
    this.map.update(ctx);
  }

  /** The map opens over play and nothing else. Behind the veil, nothing opens. */
  private canOpenMap(): boolean {
    return this.runActive && (this.current === 'hud' || this.current === 'levelcard') && !this.fade.closed;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    for (const off of this.unsubscribe) off();
    this.unsubscribe.length = 0;
    this.xr.dispose();
    this.card.dispose();
    this.map.dispose();
    this.el.remove();
  }

  /* ---------------------------------------------------------------- *
   * Wiring
   * ---------------------------------------------------------------- */

  private wireEvents(): void {
    const on = this.events.on.bind(this.events);
    this.unsubscribe.push(
      on('ui:prompt', ({ text }) => this.hud.setPrompt(text)),
      on('ui:toast', ({ text, tone }) => this.hud.toast(text, tone ?? 'info')),
      on('ui:subtitle', ({ text, ms, speaker }) => this.hud.subtitle(text, ms, speaker)),
      on('ui:note', ({ id, title, body }) => {
        this.journal.addNote(title, body, id);
        this.openJournal();
      }),

      on('level:loaded', ({ spec, index }) => {
        this.lastSpec = spec;
        this.lastIndex = index;
        this.cardArmed = true;
        // At boot the first level is generated behind the title screen; the
        // card belongs to the run, so it waits for `game:start`.
        if (this.runActive) this.playCard();
      }),
      on('game:start', () => {
        this.runActive = true;
        this.hud.reset();
        this.journal.clear();
        // A restart onto the same seed reuses the level object, so the map
        // cannot tell it is a new run from `ctx.level` alone.
        this.map.reset();
        this.playCard();
      }),
      on('game:over', ({ reason, survived, depth }) => {
        // The run is finished. Without this, restarting fires `level:loaded`
        // while the flag is still set from the dead run and the level card
        // plays over the death screen, consuming itself before `game:start`.
        this.runActive = false;
        this.card.stop();
        this.map.close();
        this.hud.setPrompt(null);
        // A run cannot end mid-descent — the simulation is halted for all of it
        // — but the death screen behind a black sheet would be unrecoverable,
        // so the veil is dropped here as well as by `game.ts`.
        this.fade.veil(0, 0.3);
        this.dead.setResult(reason, survived, depth, this.levelLabel(depth));
      }),
    );
  }

  private playCard(): void {
    if (!this.cardArmed) return;
    this.cardArmed = false;
    this.show('levelcard');
  }

  private levelLabel(depth: number): string {
    if (this.lastSpec && this.lastIndex === depth) return `${this.lastSpec.name} — ${this.lastSpec.subtitle}`;
    return `Level ${depth}`;
  }

  private dispatch(a: UIAction): void {
    for (const h of this.handlers) h(a);
  }

  private applySettings(s: GameSettings): void {
    this.settings = cloneSettings(s);
    this.hud.setSubtitlesEnabled(this.settings.subtitles);
    this.xr.settingsChanged();
    this.dispatch({ type: 'settings-changed', settings: cloneSettings(this.settings) });
  }

  private openSettings(): void {
    this.settingsReturn = this.current === 'settings' ? this.settingsReturn : this.current;
    this.show('settings');
  }

  private openJournal(): void {
    if (this.current === 'journal') return;
    this.journalReturn = this.current === 'paused' ? 'paused' : 'hud';
    this.show('journal');
  }

  /* ---------------------------------------------------------------- *
   * The two screens with no bound action
   *
   * The pack and the field notes are the only things that open mid-run off a
   * bare key rather than an `ActionName`, so they are the only two a device
   * with no keyboard cannot otherwise reach. These are the door. They are the
   * same code the `keydown` handler runs — it calls them — so a key and a thumb
   * cannot drift apart, and the keyboard path is byte-for-byte what it was.
   * ---------------------------------------------------------------- */

  /**
   * Play, as the two mid-run screens define it.
   *
   * A level card is still gameplay: everything the HUD binds stays bound.
   * Behind a closed veil nothing is — the descent owns the screen, and opening
   * the pack over the black would leave a panel floating on nothing while the
   * floor changed underneath it.
   */
  private inPlay(): boolean {
    return (this.current === 'hud' || this.current === 'levelcard') && !this.fade.closed;
  }

  /** Open the pack over play, or close it again. */
  togglePack(): void {
    // The map owns the screen while it is up, exactly as it does for the key.
    if (this.map.open) return;
    if (this.inPlay()) this.show('inventory');
    else if (this.current === 'inventory') this.show('hud');
  }

  /** Open the field notes over play, or close them again. */
  toggleNotes(): void {
    if (this.map.open) return;
    if (this.inPlay()) this.openJournal();
    else if (this.current === 'journal') this.show(this.journalReturn);
  }

  private applyScreen(screen: ScreenName, force = false): void {
    if (screen === this.current && !force) return;
    this.screens[this.current]?.exit?.();
    this.blurInside();
    // The map belongs to play. A pause menu, a death or a descent takes it away
    // rather than leaving it stacked underneath — including when Escape opens
    // the pause menu, which `game.ts` owns and this cannot intercept.
    if (screen !== 'hud' && screen !== 'levelcard') this.map.close();
    this.current = screen;
    for (const [name, s] of this.screenList) {
      s.el.hidden = name !== screen;
    }
    // The HUD stays up under the level card, and nowhere else.
    this.hud.el.hidden = screen !== 'hud' && screen !== 'levelcard';
    this.syncBlocking();
    this.screens[screen]?.enter?.();
  }

  /** `.blocking` hides the level card; the map raises the flag as a screen does. */
  private syncBlocking(): void {
    this.el.classList.toggle('blocking', this.blocking);
  }

  /** A button left focused would fire on the next Space or Enter mid-game. */
  private blurInside(): void {
    const active = document.activeElement;
    if (active instanceof HTMLElement && this.el.contains(active)) active.blur();
  }

  private syncSlots(inv: readonly InventorySlot[]): void {
    let changed = false;
    for (let i = 0; i < this.slotViews.length; i++) {
      const src = inv[i];
      const count = src?.count ?? 0;
      const kind = count > 0 && src ? src.kind : null;
      const view = this.slotViews[i]!;
      if (view.kind !== kind || view.count !== count) {
        view.kind = kind;
        view.count = count;
        changed = true;
      }
    }
    if (!changed) return;
    this.hud.setSlots(this.slotViews);
    this.inventory.setSlots(this.slotViews);
  }

  /* ---------------------------------------------------------------- *
   * Keyboard
   * ---------------------------------------------------------------- */

  private onKeyDown = (e: KeyboardEvent): void => {
    // Deliberately no `defaultPrevented` check: core/input.ts listens first and
    // already cancels Tab to stop focus traversal, which is not the same as
    // having handled it.
    const target = e.target;
    // The seed field owns its own keys.
    if (target instanceof HTMLInputElement && target.type === 'text') {
      if (this.screens[this.current]?.key?.(e)) e.preventDefault();
      return;
    }

    // The map is a stop, not a layer. Nothing else opens over it and no slot is
    // spent behind it; M closes it, and Escape reaches game.ts as it always
    // does, which puts the pause menu up and takes the map down with it.
    if (this.map.open) {
      if (this.map.key(e)) {
        e.preventDefault();
        return;
      }
      if (e.code === 'Tab' || e.code === 'KeyJ' || e.code.startsWith('Digit')) {
        if (e.code === 'Tab') e.preventDefault();
        return;
      }
    }

    // See `inPlay`. The map case above has already returned by here, so the two
    // toggles below cannot be reached with the map up and their own guard on it
    // is redundant from this direction — it is there for the other caller.
    const inPlay = this.inPlay();

    if (e.code === 'Tab') {
      if (inPlay || this.current === 'inventory') {
        e.preventDefault();
        this.togglePack();
        return;
      }
    }

    if (e.code === 'KeyJ') {
      if (inPlay || this.current === 'journal') {
        this.toggleNotes();
        return;
      }
    }

    // Escape belongs to game.ts once a run is under way; before that the UI
    // has to be able to back out of its own screens.
    if (e.code === 'Escape' && !this.runActive) {
      if (this.current === 'settings') this.show(this.settingsReturn);
      else if (this.current === 'journal') this.show(this.journalReturn);
      return;
    }

    const quickable = inPlay || this.current === 'inventory';
    if (quickable && e.code.startsWith('Digit') && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const index = Number(e.code.slice(5)) - 1;
      // The HUD only binds the four quick slots; the inventory binds all of them.
      const limit = this.current === 'inventory' ? this.slotViews.length : 4;
      if (index >= 0 && index < limit && this.useSlot(index)) return;
    }

    // A focused button already handles Enter and Space by itself.
    const active = document.activeElement;
    if ((e.code === 'Enter' || e.code === 'Space') && active instanceof HTMLButtonElement && this.el.contains(active)) {
      return;
    }

    if (this.screens[this.current]?.key?.(e)) e.preventDefault();
  };

  /** Returns false when the slot holds nothing worth spending a keystroke on. */
  private useSlot(index: number): boolean {
    const view = this.slotViews[index];
    if (!view?.kind || view.count <= 0 || !ITEM_UI[view.kind].usable) return false;
    this.dispatch({ type: 'use-slot', index });
    return true;
  }
}

/**
 * The pack and the field notes, as a pair of doors.
 *
 * They are the only two screens that open mid-run off a bare key rather than an
 * `ActionName`, and therefore the only two a device with no keyboard cannot
 * reach through `core/input.ts`. `game.ts` hands these to the on-screen touch
 * controls, which used to get at them by synthesising `KeyboardEvent`s onto
 * `window` — a keystroke that the input manager also saw and had to be
 * carefully released again.
 */
export interface ScreenDoors {
  /** Open the pack over play, or close it again. */
  togglePack(): void;
  /** Open the field notes over play, or close them again. */
  toggleNotes(): void;
}

/**
 * The return type is widened past `UISystem` rather than `core/systems.ts` being
 * edited to carry `Veil`: the level-flow code owns the descent sequence and the
 * UI owns the screen it happens on, and neither owns the system contracts. A
 * `UISystem & Veil & ScreenDoors` is still assignable to `MakeUISystem`'s
 * return, so nothing that only wants a `UISystem` is affected.
 */
export function makeUI(root: HTMLElement, events: EventBus, comfort: ComfortHost): UISystem & Veil & ScreenDoors {
  return new UI(root, events, comfort);
}
